/**
 * Frontend-safe statistics utilities.
 * This file contains NO Joi imports and can be safely imported by frontend code.
 * For Joi validation schemas, see statistics.ts (backend only).
 */
import _ from 'lodash';
import moment from 'moment';
import { Expansions, Patches } from '../../../shared/common/enums';
import { RaidDifficulty } from '../../../shared/gameConfig/sharedTypes';
import { SpellCategory } from '../../../shared/types/spellTypes';
import {
  AbortedRunsGroupBy,
  DungeonSuccessRatesGroupBy,
  EventInsightsGroupCompositionMode,
  IGetStatisticsDataQuery,
  RaidingGroupCompsMode,
  RunsOverTimeGroupBy,
  StatisticsCharacterDataTypes,
  StatisticsDataType,
  StatisticsEventInsightsDataTypes,
  StatisticsMythicPlusDataTypes,
  StatisticsRaidingDataTypes,
  StatisticsScope,
  TGetStatsCharacterDataParams,
  TGetStatsEventInsightsDataParams,
  TGetStatsMythicPlusDataParams,
  TGetStatsRaidingDataParams,
  TGetTierTalentsResponseData,
  TStatsPatronCheck
} from '../../../shared/types/statisticsTypes';
import { ExtendedSpecRole } from '../../../shared/types/wowTypes';
import { RAID_METADATA, SEASONS_BY_EXACT_SLUGS, SEASONS_SORTED } from './enums';
import { getEntireSeasonWeeks, getTotalWeeksOfRaid, getTotalWeeksOfSeason } from './game-week';

export const ENABLE_REGION_FILTER = true;
export const ENABLE_PATCH_FILTER = true;

// undefined - means all regions
export const DEFAULT_STATISTICS_SEASON_REGION: string | undefined = undefined;
export const STATISTICS_REGIONS: (string | undefined)[] = [undefined, 'us', 'eu', 'kr', 'tw', 'cn'];

export const AVAILABLE_STATISTICS_SEASONS = SEASONS_SORTED.filter(
  (season) =>
    (season.patchId <= Patches.Current && !season.hidden && moment.utc().isAfter(season.starts.us)) || season.forceShow
);
export const AVAILABLE_STATISTICS_SEASONS_SLUGS = AVAILABLE_STATISTICS_SEASONS.map((season) => season.slug);
export const DEFAULT_STATISTICS_SEASON_SLUG = AVAILABLE_STATISTICS_SEASONS.find((s) => s.isMainSeason)!.slug;

const SPECIFICALLY_OMITTED_RAIDS_FROM_STATS = new Set([
  // BRD was the anniversary joke raid - not something we care about showing stats for
  'blackrock-depths',

  // while we technically had live tracking testing in Ny'alotha,
  // we didn't have for real penetration in top guilds until Shadowlands
  'nyalotha-the-waking-city'
]);

export const AVAILABLE_STATISTICS_RAIDS = RAID_METADATA.filter((raid) => {
  if (!raid.supportsLogging) {
    return false;
  }

  if (raid.fatedInterval) {
    // fated raids are, effectively, noise - so don't show them
    return false;
  }

  if (SPECIFICALLY_OMITTED_RAIDS_FROM_STATS.has(raid.slug)) {
    return false;
  }

  if (raid.starts.us.isAfter(moment.utc())) {
    return false;
  }

  return true;
});

export const AVAILABLE_STATISTICS_RAIDS_SLUGS = AVAILABLE_STATISTICS_RAIDS.map((raid) => raid.slug);
export const DEFAULT_STATISTICS_RAID_SLUG = AVAILABLE_STATISTICS_RAIDS.concat().sort((a, b) => b.ordinal - a.ordinal)[0]
  .slug;

// TODO: TEMPORARY, update to actual
export const AVAILABLE_CHARACTER_SEASONS = AVAILABLE_STATISTICS_SEASONS.filter((season) =>
  ['season-mn-1', 'season-tww-3', 'season-tww-3-cutoffs'].includes(season.slug)
);
export const AVAILABLE_CHARACTER_SEASONS_SLUGS = AVAILABLE_CHARACTER_SEASONS.map((season) => season.slug);
export const AVAILABLE_CHARACTER_RAIDS = AVAILABLE_STATISTICS_RAIDS.filter((raid) =>
  ['tier-mn-1', 'manaforge-omega'].includes(raid.slug)
);
export const AVAILABLE_CHARACTER_RAIDS_SLUGS = AVAILABLE_CHARACTER_RAIDS.map((raid) => raid.slug);

// Narrower set of seasons/raids actually exposed on the Specs page. Both the frontend filter
// config and the backend `generateCharacterStatisticsData` tool consume these so the
// permutation matrix matches what the UI can request.
const SPEC_PAGE_VISIBLE_SEASON_SLUGS = ['season-mn-1'] as const;
const SPEC_PAGE_VISIBLE_RAID_SLUGS = ['tier-mn-1'] as const;

export const SPEC_PAGE_VISIBLE_SEASONS = AVAILABLE_CHARACTER_SEASONS.filter((season) =>
  (SPEC_PAGE_VISIBLE_SEASON_SLUGS as readonly string[]).includes(season.slug)
);
export const SPEC_PAGE_VISIBLE_SEASONS_SLUGS = SPEC_PAGE_VISIBLE_SEASONS.map((season) => season.slug);
export const SPEC_PAGE_VISIBLE_RAIDS = AVAILABLE_CHARACTER_RAIDS.filter((raid) =>
  (SPEC_PAGE_VISIBLE_RAID_SLUGS as readonly string[]).includes(raid.slug)
);
export const SPEC_PAGE_VISIBLE_RAIDS_SLUGS = SPEC_PAGE_VISIBLE_RAIDS.map((raid) => raid.slug);

export const SPEC_PAGE_DEFAULT_SEASON_SLUG = SPEC_PAGE_VISIBLE_SEASONS_SLUGS[0];
export const SPEC_PAGE_DEFAULT_RAID_SLUG = SPEC_PAGE_VISIBLE_RAIDS_SLUGS[0];

export const AVAILABLE_SPEC_POPULARITY_SEASONS = AVAILABLE_STATISTICS_SEASONS.filter((season) => season.supportsSpecs);
export const AVAILABLE_SPEC_POPULARITY_SEASONS_SLUGS = AVAILABLE_SPEC_POPULARITY_SEASONS.map((season) => season.slug);

export const AVAILABLE_GROUP_COMP_SEASONS = AVAILABLE_STATISTICS_SEASONS.filter(
  (season) => season.supportsRunRaceAndItemLevel
);
export const AVAILABLE_GROUP_COMP_SEASONS_SLUGS = AVAILABLE_GROUP_COMP_SEASONS.map((season) => season.slug);

export const AVAILABLE_HEATMAP_SEASONS = AVAILABLE_STATISTICS_SEASONS.filter((season) => season.supportsHeatmap);
export const AVAILABLE_HEATMAP_SEASONS_SLUGS = AVAILABLE_HEATMAP_SEASONS.map((season) => season.slug);

export const STATISTICS_RAIDING_DIFFICULTIES = [
  RaidDifficulty.MYTHIC,
  RaidDifficulty.HEROIC,
  RaidDifficulty.NORMAL
] as const;

export const DEFAULT_MIN_MYTHIC_LEVEL = 2;
export const DEFAULT_MAX_MYTHIC_LEVEL = 99;

export const DEFAULT_MIN_ITEM_LEVEL = 1;
export const DEFAULT_MAX_ITEM_LEVEL = 999;
export const MIN_VISIBLE_TIER_TALENT_DISTINCT_CHARACTER_COUNT = 3;

export const STATISTICS_DATA_VERSIONS: Record<StatisticsDataType, number> = {
  [StatisticsDataType.MythicPlusDungeonSuccessRates]: 3,
  [StatisticsDataType.MythicPlusRunsOverTime]: 4,
  [StatisticsDataType.MythicPlusSpecPopularity]: 3,
  [StatisticsDataType.MythicPlusGroupComps]: 3,
  [StatisticsDataType.MythicPlusHeatmaps]: 1,
  [StatisticsDataType.MythicPlusAbortedRuns]: 8,
  [StatisticsDataType.EventInsightsGroupComps]: 2,
  [StatisticsDataType.EventInsightsMaps]: 1,
  [StatisticsDataType.EventInsightsPopularSpecs]: 1,
  [StatisticsDataType.MythicPlusEncounterAttempts]: 3,
  [StatisticsDataType.RaidEncounterAttempts]: 3,
  [StatisticsDataType.RaidSpecPopularity]: 1,
  [StatisticsDataType.RaidGroupComps]: 3,

  [StatisticsDataType.TierTalents]: 12,
  [StatisticsDataType.TierGear]: 1,
  [StatisticsDataType.TierTrinkets]: 1
};

export const STATISTICS_USER_DATA_VERSIONS: Record<StatisticsDataType, string> = {
  [StatisticsDataType.MythicPlusDungeonSuccessRates]: '20260306_2',
  [StatisticsDataType.MythicPlusRunsOverTime]: '20260306_2',
  [StatisticsDataType.MythicPlusSpecPopularity]: '20260306_2',
  [StatisticsDataType.MythicPlusGroupComps]: '20260306_2',
  [StatisticsDataType.MythicPlusHeatmaps]: '',
  [StatisticsDataType.MythicPlusAbortedRuns]: '20260306_2',
  [StatisticsDataType.EventInsightsGroupComps]: '',
  [StatisticsDataType.EventInsightsMaps]: '',
  [StatisticsDataType.EventInsightsPopularSpecs]: '',
  [StatisticsDataType.MythicPlusEncounterAttempts]: '20260306_2',
  [StatisticsDataType.RaidEncounterAttempts]: '20260306_2',
  [StatisticsDataType.RaidSpecPopularity]: '20260306_2',
  [StatisticsDataType.RaidGroupComps]: '20260306_2',

  [StatisticsDataType.TierTalents]: '',
  [StatisticsDataType.TierGear]: '',
  [StatisticsDataType.TierTrinkets]: ''
};

export const STATISTICS_MYTHIC_PLUS_DATA_TYPES: StatisticsMythicPlusDataTypes[] = [
  StatisticsDataType.MythicPlusRunsOverTime,
  StatisticsDataType.MythicPlusDungeonSuccessRates,
  StatisticsDataType.MythicPlusSpecPopularity,
  StatisticsDataType.MythicPlusGroupComps,
  StatisticsDataType.MythicPlusHeatmaps,
  StatisticsDataType.MythicPlusAbortedRuns,
  StatisticsDataType.MythicPlusEncounterAttempts
];

export const STATISTICS_RAIDING_DATA_TYPES: StatisticsRaidingDataTypes[] = [
  StatisticsDataType.RaidSpecPopularity,
  StatisticsDataType.RaidGroupComps,
  StatisticsDataType.RaidEncounterAttempts
];

export const STATISTICS_EVENT_INSIGHTS_DATA_TYPES: StatisticsEventInsightsDataTypes[] = [
  StatisticsDataType.EventInsightsGroupComps,
  StatisticsDataType.EventInsightsMaps,
  StatisticsDataType.EventInsightsPopularSpecs
];

export const STATISTICS_CHARACTER_DATA_TYPES: StatisticsCharacterDataTypes[] = [
  StatisticsDataType.TierTalents,
  StatisticsDataType.TierGear,
  StatisticsDataType.TierTrinkets
];

export const isMythicPlusMetrics = (params: IGetStatisticsDataQuery): params is TGetStatsMythicPlusDataParams =>
  STATISTICS_MYTHIC_PLUS_DATA_TYPES.includes(params.type as StatisticsMythicPlusDataTypes);
export const isMythicPlusMetric = (type: StatisticsDataType): type is StatisticsMythicPlusDataTypes =>
  STATISTICS_MYTHIC_PLUS_DATA_TYPES.includes(type as StatisticsMythicPlusDataTypes);

export const isEventInsightsMetrics = (params: IGetStatisticsDataQuery): params is TGetStatsEventInsightsDataParams =>
  STATISTICS_EVENT_INSIGHTS_DATA_TYPES.includes(params.type as StatisticsEventInsightsDataTypes);
export const isEventInsightsMetric = (type: StatisticsDataType): type is StatisticsEventInsightsDataTypes =>
  STATISTICS_EVENT_INSIGHTS_DATA_TYPES.includes(type as StatisticsEventInsightsDataTypes);

export const isRaidingMetrics = (params: IGetStatisticsDataQuery): params is TGetStatsRaidingDataParams =>
  STATISTICS_RAIDING_DATA_TYPES.includes(params.type as StatisticsRaidingDataTypes);
export const isRaidingMetric = (type: StatisticsDataType): type is StatisticsRaidingDataTypes =>
  STATISTICS_RAIDING_DATA_TYPES.includes(type as StatisticsRaidingDataTypes);

export const isCharacterMetrics = (params: IGetStatisticsDataQuery): params is TGetStatsCharacterDataParams =>
  STATISTICS_CHARACTER_DATA_TYPES.includes(params.type as StatisticsCharacterDataTypes);
export const isCharacterMetric = (type: StatisticsDataType): type is StatisticsCharacterDataTypes =>
  STATISTICS_CHARACTER_DATA_TYPES.includes(type as StatisticsCharacterDataTypes);

export const STATISTICS_KEY_SEPARATOR = '; ';
export const STATISTICS_KEY_VALUE_SEPARATOR = ': ';

const stringWithLabel = (label: string, value: any): string => {
  let stringValue;

  if (Array.isArray(value) && value.length) {
    stringValue = value
      .concat()
      .sort((a, b) => {
        if (typeof a === 'number' && typeof b === 'number') {
          return a - b;
        }

        return String(a).localeCompare(String(b));
      })
      .join(',');
  } else if (value) {
    stringValue = value;
  }

  return stringValue ? `${label}${STATISTICS_KEY_VALUE_SEPARATOR}${stringValue}` : '';
};

export enum InternalStatisticsDataKey {
  UserDataVersion = 'userDataVersion'
}

/**
 * Generates a unique cache key for statistics data queries.
 *
 * ❗️❗️❗️IMPORTANT❗️❗️❗️: This key must be reversible via statisticsServiceFactory.getStatisticDataEntityQueryByKey().
 * All parameters included in the key must use their actual query values without transformation.
 * Never convert undefined to strings like 'all' or apply any other post-processing transformations,
 * as this breaks round-trip parsing (generateKey -> parseKey -> originalParams).
 *
 * The key format: 'paramName: value; paramName: value; ...'
 * - Each parameter is serialized as 'paramName: valueString'
 * - Parameters with falsy/empty values are omitted
 * - Arrays are sorted and joined with ',' for consistency
 * - Internal metadata keys (like UserDataVersion) are excluded during parsing
 * - All values are stringified for type consistency, but may have array sorting (e.g. characterIds, specIds, etc.)
 */
export const generateStatisticsDataKey = (params: IGetStatisticsDataQuery): string => {
  const commonParams = [stringWithLabel('type', params.type), stringWithLabel('version', params.version)];

  if (isMythicPlusMetrics(params)) {
    if (!_.isEmpty(params.characterIds) || !_.isEmpty(params.groupIds) || !_.isEmpty(params.userIds)) {
      commonParams.push(
        stringWithLabel(InternalStatisticsDataKey.UserDataVersion, STATISTICS_USER_DATA_VERSIONS[params.type] || '')
      );
    }

    const commonMythicPlusMetricParams = [
      stringWithLabel('season', params.season),
      stringWithLabel('region', params.region),
      stringWithLabel('seasonWeekStart', params.seasonWeekStart),
      stringWithLabel('seasonWeekEnd', params.seasonWeekEnd),
      stringWithLabel('minMythicLevel', params.minMythicLevel),
      stringWithLabel('maxMythicLevel', params.maxMythicLevel),
      stringWithLabel('zone_id', params.zone_id),
      stringWithLabel('characterIds', params.characterIds),
      stringWithLabel('userIds', params.userIds),
      stringWithLabel('groupIds', params.groupIds),
      stringWithLabel('affixes', params.affixes)
    ];

    if (params.minGroupMembersCount && params.minGroupMembersCount > 1) {
      commonMythicPlusMetricParams.push(stringWithLabel('minGroupMembersCount', params.minGroupMembersCount));
    }

    switch (params.type) {
      case StatisticsDataType.MythicPlusDungeonSuccessRates:
        return [...commonParams, ...commonMythicPlusMetricParams, stringWithLabel('groupBy', params.groupBy)]
          .filter(Boolean)
          .join(STATISTICS_KEY_SEPARATOR);

      case StatisticsDataType.MythicPlusRunsOverTime:
        return [...commonParams, ...commonMythicPlusMetricParams, stringWithLabel('groupBy', params.groupBy)]
          .filter(Boolean)
          .join(STATISTICS_KEY_SEPARATOR);

      case StatisticsDataType.MythicPlusSpecPopularity:
        return [
          ...commonParams,
          ...commonMythicPlusMetricParams,
          stringWithLabel('uniqueCharacters', params.uniqueCharacters),
          stringWithLabel('timedOnly', params.timedOnly),
          stringWithLabel('groupBy', params.groupBy)
        ]
          .filter(Boolean)
          .join(STATISTICS_KEY_SEPARATOR);

      case StatisticsDataType.MythicPlusGroupComps:
        return [
          ...commonParams,
          ...commonMythicPlusMetricParams,
          stringWithLabel('timedOnly', params.timedOnly),
          stringWithLabel('specIds', params.specIds)
        ]
          .filter(Boolean)
          .join(STATISTICS_KEY_SEPARATOR);

      case StatisticsDataType.MythicPlusAbortedRuns:
        return [...commonParams, ...commonMythicPlusMetricParams, stringWithLabel('groupBy', params.groupBy)]
          .filter(Boolean)
          .join(STATISTICS_KEY_SEPARATOR);

      case StatisticsDataType.MythicPlusEncounterAttempts:
        return [
          ...commonParams,
          ...commonMythicPlusMetricParams,
          stringWithLabel('groupBy', params.groupBy),
          stringWithLabel('encounterSlug', params.encounterSlug)
        ]
          .filter(Boolean)
          .join(STATISTICS_KEY_SEPARATOR);

      case StatisticsDataType.MythicPlusHeatmaps:
        return [
          ...commonParams,
          ...commonMythicPlusMetricParams,
          stringWithLabel('viewType', params.viewType),
          stringWithLabel('minPlayerDeaths', params.minPlayerDeaths),
          stringWithLabel('maxPlayerDeaths', params.maxPlayerDeaths),
          stringWithLabel('minItemLevel', params.minItemLevel),
          stringWithLabel('maxItemLevel', params.maxItemLevel),
          stringWithLabel('viewTypePosition', params.viewTypePosition),
          stringWithLabel('specIds', params.specIds),
          stringWithLabel('spells', params.spells),
          stringWithLabel('includePlayerDeathSpecIds', params.includePlayerDeathSpecIds),
          stringWithLabel('includePlayerSpellIds', params.includePlayerSpellIds)
        ]
          .filter(Boolean)
          .join(STATISTICS_KEY_SEPARATOR);

      default:
        // @ts-ignore
        throw new Error(`Unknown statistics data type: ${params.type}`);
    }
  } else if (isEventInsightsMetrics(params)) {
    const commonEventInsightsParams = [
      ...commonParams,
      params.eventSlug
        ? stringWithLabel('eventSlug', params.eventSlug)
        : stringWithLabel('collectionId', params.collectionId),
      stringWithLabel('bracketSlug', params.bracketSlug),
      stringWithLabel('teamId', params.teamId),
      stringWithLabel('zoneId', params.zoneId),
      stringWithLabel('affixes', params.affixes)
    ];

    switch (params.type) {
      case StatisticsDataType.EventInsightsGroupComps:
        return [...commonEventInsightsParams, stringWithLabel('groupCompositionMode', params.groupCompositionMode)]
          .filter(Boolean)
          .join(STATISTICS_KEY_SEPARATOR);

      case StatisticsDataType.EventInsightsMaps:
        return [...commonEventInsightsParams].filter(Boolean).join(STATISTICS_KEY_SEPARATOR);

      case StatisticsDataType.EventInsightsPopularSpecs:
        return [...commonEventInsightsParams].filter(Boolean).join(STATISTICS_KEY_SEPARATOR);
    }
  } else if (isRaidingMetrics(params)) {
    if (
      !_.isEmpty(params.characterIds) ||
      !_.isEmpty(params.groupIds) ||
      !_.isEmpty(params.userIds) ||
      !_.isEmpty(params.guildIds)
    ) {
      commonParams.push(
        stringWithLabel(InternalStatisticsDataKey.UserDataVersion, STATISTICS_USER_DATA_VERSIONS[params.type] || '')
      );
    }

    const commonRaidingParams = [
      ...commonParams,
      stringWithLabel('raid', params.raid),
      stringWithLabel('encounterSlug', params.encounterSlug),
      stringWithLabel('difficulty', params.difficulty),
      stringWithLabel('region', params.region),
      stringWithLabel('seasonWeekStart', params.seasonWeekStart),
      stringWithLabel('seasonWeekEnd', params.seasonWeekEnd),
      stringWithLabel('minMembersCount', params.minMembersCount),
      stringWithLabel('maxMembersCount', params.maxMembersCount),
      stringWithLabel('characterIds', params.characterIds),
      stringWithLabel('userIds', params.userIds),
      stringWithLabel('groupIds', params.groupIds),
      stringWithLabel('guildIds', params.guildIds)
    ];

    if (params.minGroupMembersCount && params.minGroupMembersCount > 1) {
      commonRaidingParams.push(stringWithLabel('minGroupMembersCount', params.minGroupMembersCount));
    }

    switch (params.type) {
      case StatisticsDataType.RaidEncounterAttempts:
        return [...commonRaidingParams, stringWithLabel('groupBy', params.groupBy)]
          .filter(Boolean)
          .join(STATISTICS_KEY_SEPARATOR);
      case StatisticsDataType.RaidSpecPopularity:
        return [
          ...commonRaidingParams,
          stringWithLabel('uniqueCharacters', params.uniqueCharacters),
          stringWithLabel('groupBy', params.groupBy)
        ]
          .filter(Boolean)
          .join(STATISTICS_KEY_SEPARATOR);
      case StatisticsDataType.RaidGroupComps: {
        const result = [
          ...commonRaidingParams,
          stringWithLabel('specIds', params.specIds),
          stringWithLabel('groupCompositionMode', params.groupCompositionMode),
          stringWithLabel('attempts', params.attempts)
        ];

        if (params.minItemLevel && params.minItemLevel !== DEFAULT_MIN_ITEM_LEVEL) {
          result.push(stringWithLabel('minItemLevel', params.minItemLevel));
        }

        if (params.maxItemLevel && params.maxItemLevel !== DEFAULT_MAX_ITEM_LEVEL) {
          result.push(stringWithLabel('maxItemLevel', params.maxItemLevel));
        }

        return result.filter(Boolean).join(STATISTICS_KEY_SEPARATOR);
      }
    }
  } else if (isCharacterMetrics(params)) {
    const commonCharacterParams = [
      ...commonParams,
      stringWithLabel('class', params.class),
      stringWithLabel('spec', params.spec),
      stringWithLabel('region', params.region),
      stringWithLabel('realmType', params.realmType),
      stringWithLabel('seasonWeekStart', params.seasonWeekStart),
      stringWithLabel('seasonWeekEnd', params.seasonWeekEnd)
    ];

    if (params.raid) {
      commonCharacterParams.push(stringWithLabel('raid', params.raid));
      commonCharacterParams.push(stringWithLabel('difficulty', params.difficulty));
      commonCharacterParams.push(stringWithLabel('encounterSlug', params.encounterSlug));
    } else if (params.season) {
      commonCharacterParams.push(stringWithLabel('season', params.season));
      commonCharacterParams.push(stringWithLabel('minMythicLevel', params.minMythicLevel));
      commonCharacterParams.push(stringWithLabel('maxMythicLevel', params.maxMythicLevel));
      commonCharacterParams.push(stringWithLabel('zone_id', params.zone_id));
    }

    switch (params.type) {
      case StatisticsDataType.TierTalents:
        return [...commonCharacterParams].filter(Boolean).join(STATISTICS_KEY_SEPARATOR);

      case StatisticsDataType.TierGear:
        return commonCharacterParams.filter(Boolean).join(STATISTICS_KEY_SEPARATOR);

      case StatisticsDataType.TierTrinkets:
        return commonCharacterParams.filter(Boolean).join(STATISTICS_KEY_SEPARATOR);
    }
  }

  throw new Error(`Unknown statistics data type, ${JSON.stringify(params)}`);
};

export const isAvailableToFilterByCustomPeriod = (
  query: Pick<TGetStatsMythicPlusDataParams, 'season' | 'region' | 'type'> & {
    zone_id?: number | boolean;
  }
): boolean => {
  return true;
};

export type StatisticMythicLevelOption = {
  minMythicLevel: number;
  maxMythicLevel: number;
  additionalInfo?: string;
};

export const getMythicLevelOptions = (seasonSlug: string): StatisticMythicLevelOption[] => {
  if (seasonSlug.startsWith('season-tww-2') || seasonSlug.startsWith('season-tww-3')) {
    return [
      { minMythicLevel: 2, maxMythicLevel: 99 },
      { minMythicLevel: 2, maxMythicLevel: 3, additionalInfo: 'No Affixes' },
      { minMythicLevel: 4, maxMythicLevel: 6, additionalInfo: "Xal'atath's Bargain" },
      { minMythicLevel: 7, maxMythicLevel: 9, additionalInfo: '+ Fort or Tyr' },
      { minMythicLevel: 10, maxMythicLevel: 11, additionalInfo: '+ Tyr or Fort' },
      { minMythicLevel: 12, maxMythicLevel: 99, additionalInfo: "Xal'atath's Guile" },
      { minMythicLevel: 14, maxMythicLevel: 99 },
      { minMythicLevel: 16, maxMythicLevel: 99 }
    ];
  } else if (seasonSlug.startsWith('season-tww-')) {
    return [
      { minMythicLevel: 2, maxMythicLevel: 99 },
      { minMythicLevel: 2, maxMythicLevel: 3, additionalInfo: "Xal'atath's Bargain" },
      { minMythicLevel: 4, maxMythicLevel: 6, additionalInfo: '+ Fort or Tyr' },
      { minMythicLevel: 7, maxMythicLevel: 9, additionalInfo: "+ Challenger's Peril" },
      { minMythicLevel: 10, maxMythicLevel: 11, additionalInfo: '+ Tyr or Fort' },
      { minMythicLevel: 12, maxMythicLevel: 99, additionalInfo: "Xal'atath's Guile" },
      { minMythicLevel: 14, maxMythicLevel: 99 },
      { minMythicLevel: 16, maxMythicLevel: 99 }
    ];
  } else if (seasonSlug.startsWith('season-df-')) {
    return [
      { minMythicLevel: 2, maxMythicLevel: 99 },
      { minMythicLevel: 2, maxMythicLevel: 4, additionalInfo: '1 Affix' },
      { minMythicLevel: 5, maxMythicLevel: 9, additionalInfo: '2 Affixes' },
      { minMythicLevel: 10, maxMythicLevel: 99, additionalInfo: '3 Affixes' },
      { minMythicLevel: 15, maxMythicLevel: 99 },
      { minMythicLevel: 20, maxMythicLevel: 99 }
    ];
  } else if (seasonSlug.startsWith('season-sl-')) {
    return [
      { minMythicLevel: 2, maxMythicLevel: 99 },
      { minMythicLevel: 2, maxMythicLevel: 3, additionalInfo: '1 Affix' },
      { minMythicLevel: 4, maxMythicLevel: 6, additionalInfo: '2 Affixes' },
      { minMythicLevel: 7, maxMythicLevel: 9, additionalInfo: '3 Affixes' },
      { minMythicLevel: 10, maxMythicLevel: 10, additionalInfo: '4 Affixes' },
      { minMythicLevel: 10, maxMythicLevel: 14 },
      { minMythicLevel: 15, maxMythicLevel: 15 },
      { minMythicLevel: 10, maxMythicLevel: 99 },
      { minMythicLevel: 15, maxMythicLevel: 99 },
      { minMythicLevel: 20, maxMythicLevel: 99 },
      { minMythicLevel: 25, maxMythicLevel: 99 },
      { minMythicLevel: 30, maxMythicLevel: 99 }
    ];
  }

  return [
    { minMythicLevel: 2, maxMythicLevel: 99 },
    { minMythicLevel: 2, maxMythicLevel: 3, additionalInfo: '1 Affix' },
    { minMythicLevel: 4, maxMythicLevel: 6, additionalInfo: '2 Affixes' },
    { minMythicLevel: 7, maxMythicLevel: 99, additionalInfo: '3 Affixes' },
    { minMythicLevel: 10, maxMythicLevel: 99 },
    { minMythicLevel: 15, maxMythicLevel: 99 },
    { minMythicLevel: 20, maxMythicLevel: 99 }
  ];
};

export const getMythicLevelOptionsForCharacterMetrics = (seasonSlug: string): StatisticMythicLevelOption[] => {
  const mythicLevelOptions = getMythicLevelOptions(seasonSlug);

  if (seasonSlug.startsWith('season-mn')) {
    return [
      { minMythicLevel: 6, maxMythicLevel: 9 },
      { minMythicLevel: 10, maxMythicLevel: 99 },
      { minMythicLevel: 15, maxMythicLevel: 99 },
      { minMythicLevel: 20, maxMythicLevel: 99 }
    ];
  }

  return mythicLevelOptions;
};

export const getStatisticsDataSeasons = (type: StatisticsMythicPlusDataTypes | StatisticsCharacterDataTypes) => {
  if (isCharacterMetric(type)) {
    return AVAILABLE_CHARACTER_SEASONS;
  }

  if (type === StatisticsDataType.MythicPlusSpecPopularity) {
    return AVAILABLE_SPEC_POPULARITY_SEASONS;
  }

  if (type === StatisticsDataType.MythicPlusGroupComps) {
    return AVAILABLE_GROUP_COMP_SEASONS;
  }

  if (type === StatisticsDataType.MythicPlusHeatmaps) {
    return AVAILABLE_HEATMAP_SEASONS;
  }

  return AVAILABLE_STATISTICS_SEASONS;
};

type TGetDatesByStatisticsScopeResults = {
  seasonWeekStart: number;
  seasonWeekEnd: number;
};

const getWeeksByScope = (
  scope: Exclude<StatisticsScope, StatisticsScope.Custom>,
  totalWeeks: number
): TGetDatesByStatisticsScopeResults => {
  switch (scope) {
    case StatisticsScope.Season: {
      return {
        seasonWeekStart: 1,
        seasonWeekEnd: totalWeeks
      };
    }
    case StatisticsScope.ThisReset: {
      return {
        seasonWeekStart: totalWeeks,
        seasonWeekEnd: totalWeeks
      };
    }
    case StatisticsScope.LastReset: {
      return {
        seasonWeekStart: totalWeeks - 1,
        seasonWeekEnd: totalWeeks - 1
      };
    }
    case StatisticsScope.TwoResetsAgo: {
      return {
        seasonWeekStart: totalWeeks - 2,
        seasonWeekEnd: totalWeeks - 2
      };
    }
    case StatisticsScope.Last3Resets: {
      return {
        seasonWeekStart: totalWeeks - 2,
        seasonWeekEnd: totalWeeks
      };
    }
    case StatisticsScope.Last4Resets: {
      return {
        seasonWeekStart: totalWeeks - 3,
        seasonWeekEnd: totalWeeks
      };
    }
    case StatisticsScope.RaceToWorldFirst: {
      return {
        seasonWeekStart: 1,
        seasonWeekEnd: Math.min(3, totalWeeks)
      };
    }
  }
};

type TGetDatesByStatisticsScopeParams = {
  scope: Exclude<StatisticsScope, StatisticsScope.Custom>;
  seasonSlug: string;
  region?: string;
};

export const getSeasonWeeksByScope = ({
  scope,
  seasonSlug,
  region: statisticRegion = DEFAULT_STATISTICS_SEASON_REGION
}: TGetDatesByStatisticsScopeParams): TGetDatesByStatisticsScopeResults => {
  const totalWeeks = getTotalWeeksOfSeason({ season: seasonSlug, region: statisticRegion });
  return getWeeksByScope(scope, totalWeeks);
};

type TGetDatesByRaidScopeParams = {
  scope: Exclude<StatisticsScope, StatisticsScope.Custom>;
  raidSlug: string;
  region?: string;
};

export const getRaidWeeksByScope = ({
  scope,
  raidSlug,
  region: statisticRegion = DEFAULT_STATISTICS_SEASON_REGION
}: TGetDatesByRaidScopeParams): TGetDatesByStatisticsScopeResults => {
  const totalWeeks = getTotalWeeksOfRaid({ raid: raidSlug, region: statisticRegion });
  return getWeeksByScope(scope, totalWeeks);
};

export const isAvailableToSpecifyZone = (
  query: Pick<IGetStatisticsDataQuery, 'type'> & {
    groupBy?: string;
  }
) => {
  if (query.type === StatisticsDataType.MythicPlusRunsOverTime) {
    return 'groupBy' in query && query.groupBy !== RunsOverTimeGroupBy.Dungeon;
  }

  if (query.type === StatisticsDataType.MythicPlusDungeonSuccessRates) {
    return 'groupBy' in query && query.groupBy !== DungeonSuccessRatesGroupBy.Dungeon;
  }

  if (query.type === StatisticsDataType.MythicPlusAbortedRuns) {
    return 'groupBy' in query && query.groupBy !== AbortedRunsGroupBy.Dungeon;
  }

  return true;
};

export const isAvailableToSpecifyMythicLevel = (
  query: Pick<IGetStatisticsDataQuery, 'type'> & {
    groupBy?: string;
  }
) => {
  return true;
};

export const getScopesWithPatreonAccess = (
  scopes: StatisticsScope[],
  checks: TStatsPatronCheck[]
): { scope: StatisticsScope; isPatreonOnly: boolean }[] => {
  return scopes.map((scope) => ({
    scope,
    isPatreonOnly: checks.includes('custom-week-range') && scope === StatisticsScope.Custom
  }));
};

export const getAllSeasonWeeksCombinationsByQuery = (
  query: Pick<TGetStatsMythicPlusDataParams, 'season' | 'region' | 'zone_id' | 'type'>
): {
  seasonWeekStart: number;
  seasonWeekEnd: number;
}[] => {
  const { season, region = DEFAULT_STATISTICS_SEASON_REGION } = query;

  if (!isAvailableToFilterByCustomPeriod(query)) {
    return [
      getEntireSeasonWeeks({
        season,
        region
      })
    ];
  }

  const endSeasonWeek = getTotalWeeksOfSeason({ season, region });
  const weeks = Array.from({ length: endSeasonWeek }, (__, i) => i + 1);

  const combinations: { seasonWeekStart: number; seasonWeekEnd: number }[] = [];

  for (let startWeek of weeks) {
    for (let endWeek of weeks) {
      if (endWeek >= startWeek) {
        combinations.push({ seasonWeekStart: startWeek, seasonWeekEnd: endWeek });
      }
    }
  }

  return combinations;
};

const buildScopesFromWeeks = (totalWeeks: number): StatisticsScope[] => {
  const result = [StatisticsScope.Season];

  const weeklyScopes: Array<{ minWeeks: number; scope: StatisticsScope }> = [
    { minWeeks: 1, scope: StatisticsScope.ThisReset },
    { minWeeks: 2, scope: StatisticsScope.LastReset },
    { minWeeks: 3, scope: StatisticsScope.TwoResetsAgo },
    { minWeeks: 3, scope: StatisticsScope.Last3Resets },
    { minWeeks: 4, scope: StatisticsScope.Last4Resets },
    { minWeeks: 1, scope: StatisticsScope.RaceToWorldFirst }
  ];

  for (const { minWeeks, scope } of weeklyScopes) {
    if (totalWeeks >= minWeeks) {
      result.push(scope);
    }
  }

  result.push(StatisticsScope.Custom);

  return result;
};

export const getAvailableScopesByQuery = (
  query: Pick<TGetStatsMythicPlusDataParams, 'season' | 'region'> | Pick<TGetStatsRaidingDataParams, 'raid' | 'region'>
): StatisticsScope[] => {
  if ('raid' in query && query.raid) {
    const totalWeeks = getTotalWeeksOfRaid({
      raid: query.raid,
      region: query.region
    });

    return buildScopesFromWeeks(totalWeeks);
  }

  if ('season' in query && query.season) {
    const totalWeeks = getTotalWeeksOfSeason({
      season: query.season,
      region: query.region
    });

    return buildScopesFromWeeks(totalWeeks);
  }

  return [];
};

export const getScopeWeeksCombinationsByQuery = (
  query: Pick<TGetStatsMythicPlusDataParams, 'season' | 'region' | 'zone_id' | 'type'>
): {
  seasonWeekStart: number;
  seasonWeekEnd: number;
}[] => {
  const availableScopes = getAvailableScopesByQuery(query).filter(
    (scope) => scope !== StatisticsScope.Custom
  ) as Exclude<StatisticsScope, StatisticsScope.Custom>[];

  const combinations: { seasonWeekStart: number; seasonWeekEnd: number }[] = [];

  for (const scope of availableScopes) {
    const { seasonWeekStart, seasonWeekEnd } = getSeasonWeeksByScope({
      scope,
      seasonSlug: query.season,
      region: query.region
    });

    combinations.push({ seasonWeekStart, seasonWeekEnd });
  }

  return combinations;
};

export const checkIsPrevExpansionSeason = (seasonSlug: string) => {
  const season = SEASONS_BY_EXACT_SLUGS[seasonSlug];
  return season.expansionId !== Expansions.Current;
};

export const DEFAULT_SPELLS_FOR_MYTHIC_PLUS_HEATMAPS = [SpellCategory.Bloodlust];

export const isEventInsightMetricsBasedOnBracketMatches = (type: StatisticsEventInsightsDataTypes): boolean => {
  return [
    StatisticsDataType.EventInsightsGroupComps,
    StatisticsDataType.EventInsightsMaps,
    StatisticsDataType.EventInsightsPopularSpecs
  ].includes(type);
};

export const isZoneFilterRequired = (filters: { type: StatisticsDataType; groupBy?: string }) => {
  return (
    filters.type === StatisticsDataType.MythicPlusHeatmaps ||
    filters.type === StatisticsDataType.RaidEncounterAttempts ||
    filters.type === StatisticsDataType.MythicPlusEncounterAttempts
  );
};

export const isEncounterFilterRequired = (filters: { type: StatisticsDataType }) => {
  return (
    filters.type === StatisticsDataType.MythicPlusEncounterAttempts ||
    filters.type === StatisticsDataType.RaidEncounterAttempts
  );
};

export const GROUP_COMP_MODE_ROLES_MAP: Record<
  RaidingGroupCompsMode | EventInsightsGroupCompositionMode,
  ExtendedSpecRole[]
> = {
  [RaidingGroupCompsMode.HEALER]: [ExtendedSpecRole.Healer],
  [RaidingGroupCompsMode.TANK]: [ExtendedSpecRole.Tank],
  [RaidingGroupCompsMode.HEALER_TANK]: [ExtendedSpecRole.Healer, ExtendedSpecRole.Tank],
  [EventInsightsGroupCompositionMode.DPS]: [ExtendedSpecRole.MeleeDps, ExtendedSpecRole.RangedDps],
  [EventInsightsGroupCompositionMode.MELEE_DPS]: [ExtendedSpecRole.MeleeDps],
  [EventInsightsGroupCompositionMode.RANGED_DPS]: [ExtendedSpecRole.RangedDps]
};

export const getMinMaxGroupMembersOptions = (min: number, max: number) => {
  if (min === max) {
    return [[min, max]];
  }

  // Data-driven buckets based on heroic encounter distribution:
  // - Small flex (10-14): 16.6%
  // - Medium flex (15-19): 23.7%
  // - 20 exact: 18.4% (mythic roster practice)
  // - Large flex (21-25): 23.0%
  // - Max flex (26-30): 18.4%
  return [
    [min, max], // All sizes
    [10, 14], // Small flex
    [15, 19], // Medium flex
    [20, 20], // 20 exact (mythic-size)
    [21, 25], // Large flex
    [26, 30] // Max flex
  ];
};

export const isTierResponseGenerating = <T extends { status?: string }>(
  data: T
): data is Extract<T, { status: 'generating' }> => 'status' in data && data.status === 'generating';

export const isTierResponseNotGenerated = <T extends { status?: string }>(
  data: T
): data is Extract<T, { status: 'not-generated' }> => 'status' in data && data.status === 'not-generated';

export const isTierResponseNoData = <T extends { status?: string }>(
  data: T
): data is Extract<T, { status: 'no-data' }> => 'status' in data && data.status === 'no-data';

export const isTierResponseShouldRefresh = (data: { status?: string }): boolean =>
  'status' in data && (data.status === 'updating' || data.status === 'generating');

export const isTierResponseSuccess = <T extends object>(data: T): data is Extract<T, { data: unknown }> =>
  'data' in data && (data as { data?: unknown }).data !== undefined;

export const SPEC_STATISTICS_PREFERRED_SCOPES = [
  StatisticsScope.Last3Resets,
  StatisticsScope.ThisReset,
  StatisticsScope.LastReset
] as const;

export const isTierTalentsResponseDataValid = (data: unknown): data is TGetTierTalentsResponseData => {
  return Boolean(typeof data === 'object' && data && 'items' in data && Array.isArray(data.items));
};
